import { useEffect } from 'react';
import { Navigate, Outlet, Route, Routes, useLocation } from 'react-router';
import { localizePath, splitLocalePath } from '@shop/shared';
import { AnnouncementBar, Header } from './components/layout/Header';
import { Footer } from './components/layout/Footer';
import { useLocale, useT } from './lib/i18n';
import { ShopPage } from './pages/Shop';
import { ProductPage } from './pages/Product';
import { ContentPage } from './pages/ContentPage';
import { NotFoundPage } from './pages/NotFound';

/** Scrolls to the top on navigation, but not when only the language changes. */
function ScrollToTop() {
  const { pathname } = useLocation();
  const page = splitLocalePath(pathname).path;
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' as ScrollBehavior });
  }, [page]);
  return null;
}

/** The home page lists every item, so the old /shop address leads there. */
function ShopRedirect() {
  const { search } = useLocation();
  const locale = useLocale();
  return <Navigate to={{ pathname: localizePath('/', locale), search }} replace />;
}

function Layout() {
  const t = useT();
  return (
    <div className="flex min-h-screen flex-col">
      <a href="#main" className="sr-only z-50 bg-ink px-4 py-2 text-paper focus:not-sr-only focus:fixed focus:top-2 focus:left-2">
        {t.skipToContent}
      </a>
      <AnnouncementBar />
      <Header />
      <main id="main" className="flex-1">
        <Outlet />
      </main>
      <Footer />
    </div>
  );
}

// The same pages are served in English at /… and in Lithuanian at /lt/….
const pages = (
  <>
    <Route element={<Layout />}>
      <Route index element={<ShopPage key="all" mode="all" />} />
      <Route path="shop" element={<ShopRedirect />} />
      <Route path="search" element={<ShopPage key="search" mode="search" />} />
      <Route path="categories/:slug" element={<ShopPage key="category" mode="category" />} />
      <Route path="collections/:slug" element={<ShopPage key="collection" mode="collection" />} />
      <Route path="products/:slug" element={<ProductPage />} />
      <Route path="pages/:slug" element={<ContentPage />} />
      <Route path="*" element={<NotFoundPage />} />
    </Route>
  </>
);

export function App() {
  return (
    <>
      <ScrollToTop />
      <Routes>
        <Route path="lt">{pages}</Route>
        {pages}
      </Routes>
    </>
  );
}
